import React, { useEffect, useState } from 'react';
import { Props } from './Employment.types';
import { SubPageTemplate } from '../../../partials';
import validationSchema from './validationSchema';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import { useDispatch } from 'react-redux';
import { Box, Columns } from 'modules/af_cm_ui_kit/components';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import usePepperError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { FormProvider, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../../form/yup/reactHookFormResolver';
import { updateApplicant } from '../../../../../store/cases/update/actions';
import useSaveButtonClicked from '../../../hooks/useSaveButtonClicked';
import { saveCase } from '../../../../../store/cases/actions';
import * as subPages from '../../../../../store/subPages';
import { SUBPAGES } from '../../../../../store/subPages/types';
import CheckDirty from '../../../../../components/CheckDirty/CheckDirty';

const Employment: React.FC<Props> = ({
  modulePrefix,
  data,
  initialData,
  resources,
  caseId,
  applicantId,
  applicantIndex,
}) => {
  const dispatch = useDispatch();
  const [currentApplicantIndex, setCurrentApplicantIndex] = useState(applicantIndex);
  const { getErrors } = usePepperError('applicants', applicantIndex);
  const methods = useForm<Props['data']>({
    mode: 'onBlur',
    defaultValues: initialData,
    resolver: useYupValidationResolver(validationSchema),
  });

  const { reset } = methods;

  useEffect(() => {
    reset(data, {
      keepDefaultValues: currentApplicantIndex === applicantIndex,
    });
    if (currentApplicantIndex !== applicantIndex) {
      setCurrentApplicantIndex(applicantIndex);
    }
  }, [data, applicantIndex, currentApplicantIndex, reset]);

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIndex]);

  return (
    <SubPageTemplate title="Employment">
      <FormProvider<Props['data']> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(
              subPages.actions.updateApplicantSubPageIsDirty(applicantIndex, SUBPAGES.APPLICANT_EMPLOYMENT, isDirty),
            );
          }}
        />
        <form onSubmit={() => {}}>
          <Columns spacing={4}>
            <>
              <Box title="Employment status">
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_EMPLOYMENT_STATUS]}
                  options={resources.employmentStatuses}
                  required
                />
              </Box>

              <Box title="Self-employed">
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_SELF_EMPLOYED_JOB_TITLE]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_NAME]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_ADDRESS]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_SELF_EMPLOYED_NATURE]}
                  options={resources.employmentNatures}
                  required
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_SELF_EMPLOYED_START_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_BUSINESS_TYPE]}
                  options={resources.businessTypes}
                  required
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_SELF_EMPLOYED_ANTICIPATED_AGE]}
                  variant={VARIANTS_TYPES.NUMBER}
                />
              </Box>
            </>
            <>
              <Box title="Employed">
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_EMPLOYED_JOB_TITLE]}
                />

                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_EMPLOYED_COMPANY_NAME]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_EMPLOYED_COMPANY_ADDRESS]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_EMPLOYED_NATURE]}
                  options={resources.employmentNatures}
                  required
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_EMPLOYED_START_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_EMPLOYED_ANTICIPATED_AGE]}
                  variant={VARIANTS_TYPES.NUMBER}
                />
              </Box>
            </>
          </Columns>
        </form>
      </FormProvider>
    </SubPageTemplate>
  );
};

export default Employment;
